import { useEffect } from "react";
import { StyleSheet } from "react-native";
import Markdown from "react-native-markdown-display";
import Animated, {
  ReduceMotion,
  runOnUI,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from "react-native-reanimated";
import { Button, styled, Text, View, ViewProps, XStack } from "tamagui";

import { AiAvatarIcon } from "../../../icons";

export const TypingDots: React.FC<ViewProps> = ({ style, ...rest }) => {
  const dot1 = useSharedValue(1);
  const dot2 = useSharedValue(1);
  const dot3 = useSharedValue(1);

  useEffect(() => {
    runOnUI(() => {
      const animate = (dot: any, delay: number) => {
        dot.value = withDelay(
          delay,
          withRepeat(
            withSequence(
              withTiming(0.7, { duration: 250 }),
              withTiming(1, { duration: 250 }),
            ),
            -1,
            false,
            () => {},
            ReduceMotion.Never,
          ),
          ReduceMotion.Never,
        );
      };

      animate(dot1, 0);
      animate(dot2, 150);
      animate(dot3, 300);
    })();
  }, [dot1, dot2, dot3]);

  const s1 = useAnimatedStyle(() => ({
    opacity: dot1.value,
    transform: [{ scale: dot1.value + 0.5 }],
  }));
  const s2 = useAnimatedStyle(() => ({
    opacity: dot2.value,
    transform: [{ scale: dot2.value + 0.5 }],
  }));
  const s3 = useAnimatedStyle(() => ({
    opacity: dot1.value,
    transform: [{ scale: dot3.value + 0.5 }],
  }));

  return (
    <View
      style={[
        {
          width: "auto",
          flexDirection: "row",
          gap: 6,
          justifyContent: "flex-start",
          alignItems: "center",
        },
        style,
      ]}
      paddingHorizontal={"$0.3"}
      {...rest}
    >
      <Animated.View style={[dotStyle, s1]} />
      <Animated.View style={[dotStyle, s2]} />
      <Animated.View style={[dotStyle, s3]} />
    </View>
  );
};

const dotStyle = {
  backgroundColor: "#CDD3DC",
  borderRadius: 50,
  width: 6,
  height: 6,
};

const MessageBubble = styled(View, {
  name: "MessageBubble",
  paddingVertical: "$2",
  paddingHorizontal: "$3",
  borderRadius: "$5",
  gap: "$1",
  flexShrink: 1,
  flex: 1,
  variants: {
    variant: {
      user: {
        color: "white",
        backgroundColor: "$green-500",
      },
      robot: {
        color: "$gray-primary-900",
        backgroundColor: "$gray-50",
        borderWidth: "$0.1",
        borderColor: "$gray-100",
      },
    },
  } as const,
  defaultVariants: {
    variant: "robot",
  },
});

const MessageText = styled(Text, {
  name: "MessageText",
  fontFamily: "$subtitle",
  fontWeight: "$4",
  fontSize: "$2",
  lineHeight: "$2",
  color: "inherit",
});

const TimeText = styled(Text, {
  name: "MessageText",
  fontFamily: "$body",
  fontWeight: "$4",
  fontSize: "$1",
  lineHeight: "$1",
  color: "$gray-secondary-400",
});

const MessageContainer = styled(View, {
  width: "100%",
  marginTop: "$1.2",
  marginBottom: "$1.2",
  variants: {
    variant: {
      user: {
        alignItems: "flex-end",
      },
      robot: {
        alignItems: "flex-start",
      },
    },
  } as const,
  defaultVariants: {
    variant: "robot",
  },
});

const Avatar = styled(View, {
  name: "Avatar",
  backgroundColor: "$gray-200",
  height: "$4",
  width: "$4",
  borderRadius: "$2",
  justifyContent: "center",
  alignItems: "center",
});

const AvatarText = styled(Text, {
  name: "AvatarText",
  fontFamily: "$subtitle",
  fontWeight: "$4",
  fontSize: "$2",
  lineHeight: "$2",
  textAlign: "center",
  color: "$gray-primary-900",
});

const baseMarkdownStyles = {
  hr: {
    marginTop: 12,
    marginBottom: 12,
  },
  heading1: {
    marginTop: 4,
    marginBottom: 4,
  },
  heading2: {
    marginTop: 4,
    marginBottom: 4,
  },
  heading3: {
    marginTop: 4,
    marginBottom: 4,
  },
};

const userMarkdownStyles = StyleSheet.create({
  ...baseMarkdownStyles,
  text: {
    color: "white",
  },
});

const robotMarkdownStyles = StyleSheet.create({
  ...baseMarkdownStyles,
  text: {
    color: "$gray-primary-900",
  },
});

export const Message: React.FC<{
  mine: boolean;
  messageText: string;
  atText: string;
  userInitial?: string;
  children?: React.ReactNode;
}> = ({ mine, messageText, atText, userInitial, children }) => {
  const variant = mine ? "user" : "robot";
  return (
    <MessageContainer variant={variant} gap={"$2"}>
      <XStack gap={"$2"}>
        {variant === "robot" && (
          <Avatar>
            <AiAvatarIcon />
          </Avatar>
        )}
        <MessageBubble variant={variant}>
          <MessageText>
            <Markdown style={mine ? userMarkdownStyles : robotMarkdownStyles}>
              {messageText}
            </Markdown>
          </MessageText>
          <TimeText>{atText}</TimeText>
          {children}
        </MessageBubble>
        {variant === "user" && (
          <Avatar>
            <AvatarText>{userInitial?.toUpperCase()}</AvatarText>
          </Avatar>
        )}
      </XStack>
    </MessageContainer>
  );
};

export const QuickAnswers = ({
  shortAnswers,
  onPress,
}: {
  shortAnswers?: string[];
  onPress?: (val: string) => void;
}) => {
  if (!shortAnswers) {
    return null;
  }

  return (
    <XStack
      marginLeft={"$1"}
      flexWrap={"wrap"}
      flexDirection={"row"}
      gap={"$1"}
    >
      {shortAnswers?.map((answer, index) => (
        <Button
          key={index}
          style={{
            boxShadow: `
        0px 0.91px 3.18px rgba(0,0,0,0.02),
        0px 2.19px 7.65px rgba(0,0,0,0.03),
        0px 4.13px 14.4px rgba(0,0,0,0.04),
        0px 7.37px 25.69px rgba(0,0,0,0.05),
        0px 13.79px 48.04px rgba(0,0,0,0.06),
        0px 33px 115px rgba(0,0,0,0.07)`,
          }}
          backgroundColor="white"
          justifyContent="center"
          alignItems="center"
          borderWidth={"$0.1"}
          borderColor={"$gray-100"}
          minHeight={"$5"}
          borderRadius={"$5"}
          paddingHorizontal={"$2"}
          maxWidth={"100%"}
          unstyled
          padding={0}
          onPress={() => onPress?.(answer)}
        >
          <Text
            numberOfLines={2}
            fontFamily={"$body"}
            marginHorizontal={"$2"}
            marginVertical={"$1"}
          >
            {answer}
          </Text>
        </Button>
      ))}
    </XStack>
  );
};
